import { supabase } from './supabase';
import { logger } from './logger';

export interface SeedCampaign {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'archived' | 'purged';
  createdAt: string;
  accountsCount: number;
  offersCount: number;
  interactionsCount: number;
}

export interface SeedAccount {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  type: 'candidate' | 'employer';
  city: string;
  email: string;
  seedTag: string;
  createdAt: string;
  /** profiles.status : 'active' | 'suspended' | ... (suspended = désactivé) */
  status?: string;
  companyName?: string;
  photoUrl?: string | null;
  videoCount?: number;
  employerProfileId?: string;
}

export interface SeedTrace {
  profile: SeedAccount;
  videos: { id: string; status: string; createdAt: string; seedTag: string | null }[];
  interactions: { id: string; videoId: string; interaction: string; createdAt: string }[];
  swipes: { id: string; targetId: string; action: string; createdAt: string }[];
  favorites: { id: string; targetType: string; targetId: string; createdAt: string }[];
  applications: { id: string; offerId: string; status: string; createdAt: string }[];
  offers: { id: string; positionName: string; city: string; createdAt: string }[];
}

// ─── Helpers ────────────────────────────────────────────────

async function callEdgeFunction(name: string, payload: any): Promise<any> {
  logger.technical('edge_fn_call', 'seedService', { name });

  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId || !session?.access_token) throw new Error('Not authenticated');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);

  let res: Response;
  try {
    res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      }
    );
  } catch (err: any) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      logger.error('edge_fn_timeout', 'seedService', { name, timeout: '15s' });
      throw new Error(`Edge function "${name}" timeout (15s)`);
    }
    logger.error('edge_fn_network', 'seedService', { name, error: String(err) });
    throw err;
  }
  clearTimeout(timeout);

  let data: any;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok || data?.error) {
    const errMsg = data?.error || `HTTP ${res.status}`;
    logger.error('edge_fn_error', 'seedService', { name, error: errMsg, status: res.status });
    throw new Error(errMsg);
  }

  logger.technical('edge_fn_ok', 'seedService', { name, data });
  return data;
}

async function logAudit(action: string, targetType: string, targetId: string, details?: any) {
  logger.functional(action, 'seedService', { targetType, targetId, ...details });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  await supabase.from('growth_audit_log').insert({
    admin_id: session.user.id,
    action,
    target_type: targetType,
    target_id: targetId,
    details: details || {},
  });
}

// ─── Campaigns ──────────────────────────────────────────────

export async function fetchCampaigns(): Promise<SeedCampaign[]> {
  const { data, error } = await supabase
    .from('seed_campaigns')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_campaigns', 'seedService', { error: error.message });
    return [];
  }

  const campaigns = data || [];
  logger.technical('fetch_campaigns_ok', 'seedService', { count: campaigns.length });

  const countMap = (rows: any[] | null) => {
    const m: Record<string, number> = {};
    (rows || []).forEach((r: any) => {
      m[r.seed_tag] = (m[r.seed_tag] || 0) + 1;
    });
    return m;
  };

  let pMap: Record<string, number> = {};
  let oMap: Record<string, number> = {};
  let iMap: Record<string, number> = {};

  try {
    const results = await Promise.allSettled([
      supabase.from('profiles').select('seed_tag').not('seed_tag', 'is', null),
      supabase.from('job_offers').select('seed_tag').not('seed_tag', 'is', null),
      supabase.from('video_interactions').select('seed_tag').not('seed_tag', 'is', null),
    ]);
    if (results[0].status === 'fulfilled') pMap = countMap(results[0].value.data);
    if (results[1].status === 'fulfilled') oMap = countMap(results[1].value.data);
    if (results[2].status === 'fulfilled') iMap = countMap(results[2].value.data);
  } catch (err) {
    logger.error('fetch_campaigns_counts', 'seedService', { error: String(err) });
  }

  return campaigns.map((c: any) => ({
    id: c.id,
    name: c.name,
    description: c.description || '',
    status: c.status,
    createdAt: new Date(c.created_at).toLocaleDateString('fr-FR'),
    accountsCount: pMap[c.name] || 0,
    offersCount: oMap[c.name] || 0,
    interactionsCount: iMap[c.name] || 0,
  }));
}

export async function createCampaign(name: string, description: string): Promise<SeedCampaign> {
  logger.functional('create_campaign', 'seedService', { name });
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase
    .from('seed_campaigns')
    .insert({ name, description, created_by: session?.user?.id })
    .select()
    .single();

  if (error) throw error;
  await logAudit('create_campaign', 'campaign', data.id, { name });

  return {
    id: data.id,
    name: data.name,
    description: data.description || '',
    status: data.status,
    createdAt: new Date(data.created_at).toLocaleDateString('fr-FR'),
    accountsCount: 0,
    offersCount: 0,
    interactionsCount: 0,
  };
}

export interface ToggleSeedResult {
  accounts: number;
  offers: number;
  videos: number;
}

/**
 * Désactive/réactive TOUTE une campagne (réversible) : comptes bannis/débannis,
 * offres seeds fermées/réouvertes, vidéos seeds retirées/remises au feed.
 */
export async function toggleCampaign(
  campaignTag: string,
  action: 'deactivate' | 'reactivate',
): Promise<ToggleSeedResult> {
  logger.functional(`${action}_campaign`, 'seedService', { campaignTag });
  const body = await callEdgeFunction('admin-toggle-seed', { scope: 'campaign', campaignTag, action });
  await logAudit(`${action}_campaign`, 'campaign', campaignTag, {
    accounts: body.accounts, offers: body.offers, videos: body.videos,
  });
  return { accounts: body.accounts, offers: body.offers, videos: body.videos };
}

/**
 * Désactive/réactive des comptes seeds individuels (réversible), avec leur
 * contenu seed (offres/vidéos). Refusé si un id n'est pas un compte seed.
 */
export async function toggleSeedAccounts(
  userIds: string[],
  action: 'deactivate' | 'reactivate',
): Promise<ToggleSeedResult> {
  logger.functional(`${action}_seed_accounts`, 'seedService', { count: userIds.length });
  const body = await callEdgeFunction('admin-toggle-seed', { scope: 'accounts', userIds, action });
  await logAudit(`${action}_seed_accounts`, 'accounts', '', {
    userIds, accounts: body.accounts, offers: body.offers, videos: body.videos,
  });
  return { accounts: body.accounts, offers: body.offers, videos: body.videos };
}

/** @deprecated remplacé par toggleCampaign(tag, 'deactivate') — conservé pour compat. */
export async function archiveCampaign(id: string): Promise<void> {
  logger.functional('archive_campaign', 'seedService', { id });
  const { error } = await supabase
    .from('seed_campaigns')
    .update({ status: 'archived', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
  await logAudit('archive_campaign', 'campaign', id);
}

export async function purgeCampaign(campaignTag: string): Promise<void> {
  logger.functional('purge_campaign', 'seedService', { campaignTag });
  await callEdgeFunction('admin-purge-campaign', { campaignTag });
  await logAudit('purge_campaign', 'campaign', campaignTag);
}

// ─── Campaign detail ────────────────────────────────────────

export interface CampaignInteractionStats {
  views: number;
  likes: number;
  shares: number;
  swipes: number;
  favorites: number;
  applications: number;
}

export interface CampaignDetail {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'archived' | 'purged';
  createdAt: string;
  interactions: CampaignInteractionStats;
}

export async function fetchCampaignDetail(name: string): Promise<CampaignDetail | null> {
  const { data: c, error } = await supabase
    .from('seed_campaigns')
    .select('*')
    .eq('name', name)
    .maybeSingle();

  if (error || !c) {
    logger.error('fetch_campaign_detail', 'seedService', { name, error: error?.message });
    return null;
  }

  const [viRes, swRes, favRes, appRes] = await Promise.all([
    supabase.from('video_interactions').select('interaction').eq('seed_tag', name),
    supabase.from('swipe_actions').select('id').eq('seed_tag', name),
    supabase.from('favorites').select('id').eq('seed_tag', name),
    supabase.from('applications').select('id').eq('seed_tag', name),
  ]);

  const vi = viRes.data || [];
  const countBy = (type: string) => vi.filter((r: any) => r.interaction === type).length;

  return {
    id: c.id,
    name: c.name,
    description: c.description || '',
    status: c.status,
    createdAt: new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
    interactions: {
      views: countBy('view'),
      likes: countBy('like'),
      shares: countBy('share'),
      swipes: (swRes.data || []).length,
      favorites: (favRes.data || []).length,
      applications: (appRes.data || []).length,
    },
  };
}

// ─── Seed Accounts ──────────────────────────────────────────

export async function createSeedAccounts(params: {
  campaignTag: string;
  accountType: 'candidate' | 'employer';
  count: number;
  city?: string;
  sectorCode?: string;
  withVideo?: boolean;
}): Promise<{ created: number }> {
  logger.functional('create_seed_accounts', 'seedService', { ...params });
  const body = await callEdgeFunction('admin-create-seed-accounts', params);
  await logAudit('create_seed_accounts_batch', 'accounts', params.campaignTag, {
    type: params.accountType,
    count: body.created,
  });
  return { created: body.created };
}

export async function createSingleSeedAccount(params: {
  campaignTag: string;
  accountType: 'candidate' | 'employer';
  firstName: string;
  lastName: string;
  city: string;
  email?: string;
  companyName?: string;
}): Promise<{ userId: string }> {
  logger.functional('create_single_seed_account', 'seedService', { firstName: params.firstName, lastName: params.lastName, campaign: params.campaignTag });
  const body = await callEdgeFunction('admin-create-seed-accounts', {
    campaignTag: params.campaignTag,
    accountType: params.accountType,
    count: 1,
    city: params.city,
    manual: true,
    firstName: params.firstName,
    lastName: params.lastName,
    email: params.email,
    companyName: params.companyName,
  });
  await logAudit('create_seed_account_manual', 'account', body.userIds?.[0] || '', {
    name: `${params.firstName} ${params.lastName}`,
    campaign: params.campaignTag,
  });
  return { userId: body.userIds?.[0] || '' };
}

export async function updateSeedAccount(userId: string, fields: {
  firstName?: string;
  lastName?: string;
  city?: string;
  companyName?: string;
  type: 'candidate' | 'employer';
}): Promise<void> {
  logger.functional('update_seed_account', 'seedService', { userId });
  if (fields.type === 'candidate') {
    const update: any = {};
    if (fields.firstName !== undefined) update.first_name = fields.firstName;
    if (fields.lastName !== undefined) update.last_name = fields.lastName;
    if (fields.city !== undefined) update.city = fields.city;
    const { error } = await supabase
      .from('candidate_profiles')
      .update(update)
      .eq('user_id', userId);
    if (error) throw error;
  } else {
    const update: any = {};
    if (fields.firstName !== undefined) update.first_name = fields.firstName;
    if (fields.lastName !== undefined) update.last_name = fields.lastName;
    if (fields.city !== undefined) update.company_city = fields.city;
    if (fields.companyName !== undefined) update.company_name = fields.companyName;
    const { error } = await supabase
      .from('employer_profiles')
      .update(update)
      .eq('user_id', userId);
    if (error) throw error;
  }
  await logAudit('update_seed_account', 'account', userId, fields);
}

export async function fetchSeedAccounts(campaignTag?: string): Promise<SeedAccount[]> {
  let query = supabase
    .from('profiles')
    .select('id, email, role, seed_tag, created_at, status')
    .not('seed_tag', 'is', null)
    .order('created_at', { ascending: false });

  if (campaignTag) {
    query = query.eq('seed_tag', campaignTag);
  }

  const { data, error } = await query;
  if (error) {
    logger.error('fetch_seed_accounts_query', 'seedService', { error: error.message, code: error.code });
    return [];
  }

  const profiles = data || [];
  logger.technical('fetch_seed_accounts_ok', 'seedService', {
    count: profiles.length,
    campaignTag: campaignTag || 'ALL',
    sampleIds: profiles.slice(0, 3).map((p: any) => ({ id: p.id, seed_tag: p.seed_tag })),
  });
  const userIds = profiles.map((p: any) => p.id);

  const candidateMap: Record<string, any> = {};
  const employerMap: Record<string, any> = {};
  const videoCountMap: Record<string, number> = {};

  if (userIds.length > 0) {
    const [{ data: candidates }, { data: employers }] = await Promise.all([
      supabase.from('candidate_profiles').select('user_id, first_name, last_name, city, photo_url, id').in('user_id', userIds),
      supabase.from('employer_profiles').select('id, user_id, first_name, last_name, company_city, company_name').in('user_id', userIds),
    ]);
    (candidates || []).forEach((c: any) => { candidateMap[c.user_id] = c; });
    (employers || []).forEach((e: any) => { employerMap[e.user_id] = e; });

    const candidateProfileIds = (candidates || []).map((c: any) => c.id);
    if (candidateProfileIds.length > 0) {
      const { data: videos } = await supabase
        .from('candidate_videos')
        .select('candidate_id')
        .in('candidate_id', candidateProfileIds);
      (videos || []).forEach((v: any) => {
        videoCountMap[v.candidate_id] = (videoCountMap[v.candidate_id] || 0) + 1;
      });
    }
  }

  return profiles.map((p: any) => {
    const c = candidateMap[p.id];
    const e = employerMap[p.id];
    return {
      id: p.id,
      userId: p.id,
      firstName: c?.first_name || e?.first_name || '',
      lastName: c?.last_name || e?.last_name || '',
      type: p.role as 'candidate' | 'employer',
      city: c?.city || e?.company_city || '',
      email: p.email || '',
      seedTag: p.seed_tag,
      status: p.status || 'active',
      createdAt: new Date(p.created_at).toLocaleDateString('fr-FR'),
      companyName: e?.company_name || undefined,
      photoUrl: c?.photo_url || null,
      videoCount: videoCountMap[c?.id] || 0,
      employerProfileId: e?.id || undefined,
    };
  });
}

export async function deleteSeedAccount(userId: string): Promise<void> {
  logger.functional('delete_seed_account', 'seedService', { userId });
  await callEdgeFunction('admin-disable-account', { userId, disabled: true });
  await logAudit('delete_seed_account', 'account', userId);
}

export async function batchDeleteSeeds(userIds: string[]): Promise<{ deleted: number }> {
  logger.functional('batch_delete_seeds', 'seedService', { count: userIds.length, userIds });
  let deleted = 0;
  for (const uid of userIds) {
    try {
      // Delete all traces first
      await supabase.from('video_interactions').delete().eq('user_id', uid);
      await supabase.from('swipe_actions').delete().eq('user_id', uid);
      await supabase.from('favorites').delete().eq('user_id', uid);
      await supabase.from('applications').delete().eq('seed_tag', uid);

      // Get candidate_profile id for video cleanup
      const { data: cp } = await supabase.from('candidate_profiles').select('id').eq('user_id', uid).maybeSingle();
      if (cp) {
        await supabase.from('candidate_videos').delete().eq('candidate_id', cp.id);
      }

      await callEdgeFunction('admin-disable-account', { userId: uid, disabled: true });
      deleted++;
    } catch (err) {
      logger.error('batch_delete_seed_fail', 'seedService', { userId: uid, error: String(err) });
    }
  }
  await logAudit('batch_delete_seeds', 'accounts', '', { count: deleted, userIds });
  return { deleted };
}

// ─── Seed Traces ────────────────────────────────────────────

export async function fetchSeedTrace(userId: string): Promise<SeedTrace> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('id, email, role, seed_tag, created_at')
    .eq('id', userId)
    .single();

  if (!profile) throw new Error('Profil non trouvé');

  const { data: cp } = await supabase
    .from('candidate_profiles')
    .select('user_id, first_name, last_name, city, photo_url, id')
    .eq('user_id', userId)
    .maybeSingle();

  const { data: ep } = await supabase
    .from('employer_profiles')
    .select('user_id, first_name, last_name, company_city, company_name')
    .eq('user_id', userId)
    .maybeSingle();

  const seedAccount: SeedAccount = {
    id: profile.id,
    userId: profile.id,
    firstName: cp?.first_name || ep?.first_name || '',
    lastName: cp?.last_name || ep?.last_name || '',
    type: profile.role as 'candidate' | 'employer',
    city: cp?.city || ep?.company_city || '',
    email: profile.email || '',
    seedTag: profile.seed_tag || '',
    createdAt: new Date(profile.created_at).toLocaleDateString('fr-FR'),
    companyName: ep?.company_name || undefined,
    photoUrl: cp?.photo_url || null,
  };

  const [vidRes, intRes, swRes, favRes, appRes, offRes] = await Promise.all([
    cp ? supabase.from('candidate_videos').select('id, status, created_at, seed_tag').eq('candidate_id', cp.id) : { data: [] },
    supabase.from('video_interactions').select('id, video_id, interaction, created_at').eq('user_id', userId),
    supabase.from('swipe_actions').select('id, target_id, action, created_at').eq('user_id', userId),
    supabase.from('favorites').select('id, target_type, target_id, created_at').eq('user_id', userId),
    supabase.from('applications').select('id, job_offer_id, status, applied_at').eq('seed_tag', profile.seed_tag || ''),
    profile.role === 'employer'
      ? supabase.from('job_offers').select('id, position_name, establishment_city, created_at').eq('employer_id', ep?.user_id || userId).not('seed_tag', 'is', null)
      : { data: [] },
  ]);

  return {
    profile: seedAccount,
    videos: (vidRes.data || []).map((v: any) => ({
      id: v.id,
      status: v.status,
      createdAt: new Date(v.created_at).toLocaleString('fr-FR'),
      seedTag: v.seed_tag,
    })),
    interactions: (intRes.data || []).map((i: any) => ({
      id: i.id,
      videoId: i.video_id,
      interaction: i.interaction,
      createdAt: new Date(i.created_at).toLocaleString('fr-FR'),
    })),
    swipes: (swRes.data || []).map((s: any) => ({
      id: s.id,
      targetId: s.target_id,
      action: s.action,
      createdAt: new Date(s.created_at).toLocaleString('fr-FR'),
    })),
    favorites: (favRes.data || []).map((f: any) => ({
      id: f.id,
      targetType: f.target_type,
      targetId: f.target_id,
      createdAt: new Date(f.created_at).toLocaleString('fr-FR'),
    })),
    applications: (appRes.data || []).map((a: any) => ({
      id: a.id,
      offerId: a.job_offer_id,
      status: a.status,
      createdAt: new Date(a.applied_at).toLocaleString('fr-FR'),
    })),
    offers: (offRes.data || []).map((o: any) => ({
      id: o.id,
      positionName: o.position_name || '',
      city: o.establishment_city || '',
      createdAt: new Date(o.created_at).toLocaleString('fr-FR'),
    })),
  };
}

export async function deleteSeedTraceItem(table: string, id: string): Promise<void> {
  logger.functional('delete_seed_trace_item', 'seedService', { table, id });
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
  await logAudit('delete_seed_trace', table, id);
}

// ─── Seed Videos ────────────────────────────────────────────

export async function createSeedVideos(params: {
  campaignTag: string;
  candidateProfileIds: string[];
}): Promise<number> {
  logger.functional('create_seed_videos', 'seedService', { campaignTag: params.campaignTag, count: params.candidateProfileIds.length });
  const PLACEHOLDER_VIDEO = '/seed-assets/placeholder-video.mp4';
  const PLACEHOLDER_THUMB = '/seed-assets/placeholder-thumb.jpg';

  const rows = params.candidateProfileIds.map((cpId) => ({
    candidate_id: cpId,
    video_url: PLACEHOLDER_VIDEO,
    thumbnail_url: PLACEHOLDER_THUMB,
    status: 'approved',
    seed_tag: params.campaignTag,
    duration_seconds: 30 + Math.floor(Math.random() * 60),
    tags: ['seed'],
  }));

  const { error, data } = await supabase.from('candidate_videos').insert(rows).select('id');
  if (error) throw error;
  await logAudit('create_seed_videos', 'videos', params.campaignTag, {
    count: data?.length || 0,
  });
  return data?.length || 0;
}

// ─── Seed Offers (enriched) ─────────────────────────────────

const OFFER_DESCRIPTIONS: Record<string, string[]> = {
  default: [
    'Nous recherchons un(e) professionnel(le) motivé(e) pour rejoindre notre équipe dynamique. Poste à pourvoir immédiatement dans un environnement de travail agréable.',
    'Rejoignez une équipe passionnée dans un établissement reconnu. Formation assurée, évolution possible. Ambiance conviviale et management bienveillant.',
    'Opportunité unique dans un établissement en pleine croissance. Nous valorisons l\'autonomie, l\'esprit d\'équipe et le sens du service client.',
  ],
};

export async function createSeedOffer(params: {
  campaignTag: string;
  employerId: string;
  sectorCode: string;
  positionCode: string;
  positionName: string;
  city: string;
  contractType: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  workRhythm?: string;
  advantages?: string[];
}): Promise<void> {
  logger.functional('create_seed_offer', 'seedService', { positionName: params.positionName, city: params.city });
  const templates = OFFER_DESCRIPTIONS[params.sectorCode] || OFFER_DESCRIPTIONS.default;
  const autoDesc = templates[Math.floor(Math.random() * templates.length)];

  const { error } = await supabase.from('job_offers').insert({
    employer_id: params.employerId,
    sector_code: params.sectorCode,
    position_code: params.positionCode,
    position_name: params.positionName,
    establishment_city: params.city,
    contract_type: params.contractType,
    other_info: params.description || autoDesc,
    salary_min: params.salaryMin || null,
    salary_max: params.salaryMax || null,
    status: 'active',
    seed_tag: params.campaignTag,
  });
  if (error) throw error;
  await logAudit('create_seed_offer', 'offer', params.campaignTag, {
    position: params.positionName,
    city: params.city,
  });
}

export async function updateSeedOffer(offerId: string, fields: {
  positionName?: string;
  city?: string;
  contractType?: string;
  description?: string;
  salaryMin?: number | null;
  salaryMax?: number | null;
}): Promise<void> {
  const update: any = {};
  if (fields.positionName !== undefined) update.position_name = fields.positionName;
  if (fields.city !== undefined) update.establishment_city = fields.city;
  if (fields.contractType !== undefined) update.contract_type = fields.contractType;
  if (fields.description !== undefined) update.other_info = fields.description;
  if (fields.salaryMin !== undefined) update.salary_min = fields.salaryMin;
  if (fields.salaryMax !== undefined) update.salary_max = fields.salaryMax;

  const { error } = await supabase.from('job_offers').update(update).eq('id', offerId);
  if (error) throw error;
  await logAudit('update_seed_offer', 'offer', offerId, fields);
}

export async function fetchSeedOffers(campaignTag?: string): Promise<any[]> {
  let query = supabase
    .from('job_offers')
    .select('id, position_name, sector_code, establishment_city, contract_type, status, created_at, seed_tag, employer_id, other_info, salary_min, salary_max')
    .not('seed_tag', 'is', null)
    .order('created_at', { ascending: false });

  if (campaignTag) {
    query = query.eq('seed_tag', campaignTag);
  }

  const { data, error } = await query;
  if (error) {
    logger.error('fetch_seed_offers', 'seedService', { error: error.message, code: error.code });
    return [];
  }
  logger.technical('fetch_seed_offers_ok', 'seedService', { count: (data || []).length });
  return (data || []).map((o: any) => ({
    id: o.id,
    positionName: o.position_name || '',
    sectorCode: o.sector_code || '',
    city: o.establishment_city || '',
    contractType: o.contract_type || '',
    status: o.status || 'active',
    seedTag: o.seed_tag,
    employerId: o.employer_id,
    description: o.other_info || '',
    salaryMin: o.salary_min,
    salaryMax: o.salary_max,
    createdAt: new Date(o.created_at).toLocaleDateString('fr-FR'),
  }));
}

export async function deleteSeedOffer(offerId: string): Promise<void> {
  logger.functional('delete_seed_offer', 'seedService', { offerId });
  const { error } = await supabase.from('job_offers').delete().eq('id', offerId);
  if (error) throw error;
  await logAudit('delete_seed_offer', 'offer', offerId);
}

// ─── Ratio Stats ────────────────────────────────────────────

export interface SeedRatioStats {
  accounts: { seed: number; real: number };
  offers: { seed: number; real: number };
  videos: { seed: number; real: number };
  interactions: { seed: number; real: number };
  applications: { seed: number; real: number };
}

export async function fetchSeedRatioStats(): Promise<SeedRatioStats> {
  const [
    { data: allProfiles },
    { data: allOffers },
    { data: allVideos },
    { data: allInteractions },
    { data: allApps },
  ] = await Promise.all([
    supabase.from('profiles').select('seed_tag').neq('role', 'admin'),
    supabase.from('job_offers').select('seed_tag'),
    supabase.from('candidate_videos').select('seed_tag'),
    supabase.from('video_interactions').select('seed_tag'),
    supabase.from('applications').select('seed_tag'),
  ]);

  const split = (rows: any[] | null) => {
    const seed = (rows || []).filter((r) => r.seed_tag).length;
    return { seed, real: (rows || []).length - seed };
  };

  const stats = {
    accounts: split(allProfiles),
    offers: split(allOffers),
    videos: split(allVideos),
    interactions: split(allInteractions),
    applications: split(allApps),
  };
  logger.technical('fetch_seed_ratio_stats_ok', 'seedService', stats);
  return stats;
}
